import React, { useState, useCallback } from "react";
import { Helmet } from "react-helmet-async";
import { ExternalLink, RefreshCw, Eye, Loader2, CheckCircle2, AlertCircle } from "lucide-react";
import api from "../../lib/adminApi";
import { toast } from "sonner";

const EDITABLE_PAGES = [
  { label: "Ana Sayfa", path: "/tr/", emoji: "🏠" },
  { label: "Hakkımızda", path: "/tr/about", emoji: "👥" },
  { label: "Hizmetler", path: "/tr/services", emoji: "⚙️" },
  { label: "Meta Ads", path: "/tr/services/meta-ads", emoji: "📣" },
  { label: "Google Ads", path: "/tr/services/google-ads", emoji: "🔍" },
  { label: "SEO", path: "/tr/services/seo", emoji: "📈" },
  { label: "Kreatif", path: "/tr/services/creative", emoji: "🎨" },
  { label: "Sosyal Medya", path: "/tr/services/social-media", emoji: "📱" },
  { label: "Web Geliştirme", path: "/tr/services/web-development", emoji: "💻" },
  { label: "Blog", path: "/tr/blog", emoji: "📝" },
  { label: "Çalışmalarımız", path: "/tr/works", emoji: "🗂️" },
  { label: "İletişim", path: "/tr/contact", emoji: "✉️" },
  { label: "Postun Hazır", path: "/tr/postun-hazir", emoji: "✅" },
  { label: "Paketini Bul", path: "/tr/paketini-bul", emoji: "💡" },
];

const BASE_URL = process.env.REACT_APP_BACKEND_URL?.replace("/api", "") || "";
const PREVIEW_URL = BASE_URL.replace("8001", "3000");

export default function AdminLiveEditorPage() {
  const [activePage, setActivePage] = useState(EDITABLE_PAGES[0]);
  const [iframeKey, setIframeKey] = useState(0);
  const [purging, setPurging] = useState(false);
  const [purgeResult, setPurgeResult] = useState(null);
  const [iframeLoaded, setIframeLoaded] = useState(false);

  const previewSrc = `${PREVIEW_URL}${activePage.path}`;
  const productionUrl = `https://yastikmedya.com${activePage.path}`;

  const handlePurgeCache = async () => {
    setPurging(true);
    setPurgeResult(null);
    try {
      const r = await api.post("/admin/cache/purge");
      setPurgeResult({ ok: true, msg: r.data?.message });
      toast.success("Cache temizlendi!");
      // Reload iframe
      setIframeKey((k) => k + 1);
    } catch (e) {
      const msg = e?.response?.data?.detail || e.message;
      setPurgeResult({ ok: false, msg });
      toast.error("Cache temizlenemedi: " + msg);
    } finally {
      setPurging(false);
    }
  };

  const reloadIframe = () => {
    setIframeLoaded(false);
    setIframeKey((k) => k + 1);
  };

  return (
    <div data-testid="admin-live-editor" className="flex flex-col h-[calc(100vh-4rem)]">
      <Helmet><title>Canlı Düzenleyici · Yastık Medya Admin</title></Helmet>

      {/* Toolbar */}
      <div className="flex-shrink-0 flex items-center gap-3 flex-wrap border-b border-mist bg-white px-4 py-3">
        {/* Page selector */}
        <div className="flex items-center gap-1.5 flex-wrap flex-1 min-w-0">
          {EDITABLE_PAGES.map((pg) => (
            <button
              key={pg.path}
              type="button"
              onClick={() => { setActivePage(pg); setIframeLoaded(false); }}
              className={`rounded-full px-3 py-1 text-xs font-medium transition-colors whitespace-nowrap
                ${activePage.path === pg.path
                  ? "bg-ink text-cream"
                  : "bg-mist text-ink/70 hover:bg-ink/10"}`}
            >
              {pg.emoji} {pg.label}
            </button>
          ))}
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2 flex-shrink-0">
          <button
            type="button"
            onClick={reloadIframe}
            className="inline-flex items-center gap-1.5 rounded-full border border-mist px-3 py-1.5 text-xs text-ink hover:border-ink transition-colors"
            title="Yenile"
          >
            <RefreshCw className="h-3.5 w-3.5" /> Yenile
          </button>

          <button
            type="button"
            onClick={handlePurgeCache}
            disabled={purging}
            className="inline-flex items-center gap-1.5 rounded-full border-2 border-orange-300 bg-orange-50 px-3 py-1.5 text-xs font-semibold text-orange-700 hover:bg-orange-100 disabled:opacity-60 transition-colors"
            data-testid="cache-purge-btn"
            title="Tüm cache'leri temizle"
          >
            {purging
              ? <><Loader2 className="h-3.5 w-3.5 animate-spin" /> Temizleniyor…</>
              : <><RefreshCw className="h-3.5 w-3.5" /> Cache Temizle</>
            }
          </button>

          <a
            href={productionUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 rounded-full bg-ink text-cream px-3 py-1.5 text-xs font-semibold hover:bg-brand-pink transition-colors"
          >
            <ExternalLink className="h-3.5 w-3.5" /> Canlıda Aç
          </a>
        </div>
      </div>

      {/* Cache result banner */}
      {purgeResult && (
        <div className={`flex-shrink-0 flex items-center gap-2 px-4 py-2 text-sm border-b ${
          purgeResult.ok ? "bg-emerald-50 border-emerald-200 text-emerald-700" : "bg-red-50 border-red-200 text-red-700"
        }`}>
          {purgeResult.ok
            ? <CheckCircle2 className="h-4 w-4" />
            : <AlertCircle className="h-4 w-4" />}
          {purgeResult.msg}
          {purgeResult.ok && (
            <span className="ml-2 text-xs opacity-70">
              Cihazlardaki farklı görünümü çözmek için kullanıcılar Ctrl+Shift+R yapmalı veya tarayıcı cache'ini temizlemeli.
            </span>
          )}
        </div>
      )}

      {/* Info banner */}
      <div className="flex-shrink-0 bg-blue-50 border-b border-blue-200 px-4 py-2 flex items-center gap-3 flex-wrap">
        <Eye className="h-4 w-4 text-blue-600 flex-shrink-0" />
        <div className="text-xs text-blue-700 flex-1">
          <strong>Canlı Önizleme</strong> — Aşağıda sitenin preview ortamını görüyorsunuz.
          İçerik değişikliklerini Admin panelinden (İçerik/Fiyatlandırma sekmeleri) yapın → Kaydet → buradaki "Yenile" butonuna basın.
        </div>
        <a
          href="/admin/services"
          className="text-xs font-semibold text-blue-600 hover:underline whitespace-nowrap"
        >
          → Hizmet İçerikleri Düzenle
        </a>
        <a
          href="/admin/blog"
          className="text-xs font-semibold text-blue-600 hover:underline whitespace-nowrap"
        >
          → Blog Yazıları Düzenle
        </a>
        <a
          href="/admin/settings"
          className="text-xs font-semibold text-blue-600 hover:underline whitespace-nowrap"
        >
          → Site Ayarları
        </a>
      </div>

      {/* Iframe preview */}
      <div className="flex-1 relative bg-mist/30 overflow-hidden">
        {!iframeLoaded && (
          <div className="absolute inset-0 flex items-center justify-center z-10 bg-cream/80 backdrop-blur-sm">
            <div className="flex flex-col items-center gap-3">
              <Loader2 className="h-8 w-8 animate-spin text-brand-pink" />
              <p className="text-sm text-ink/60">{activePage.label} yükleniyor…</p>
            </div>
          </div>
        )}
        <iframe
          key={iframeKey}
          src={previewSrc}
          title={`Önizleme: ${activePage.label}`}
          className="w-full h-full border-0"
          onLoad={() => setIframeLoaded(true)}
          sandbox="allow-same-origin allow-scripts allow-forms allow-popups allow-popups-to-escape-sandbox"
        />
      </div>

      {/* Bottom: URL bar */}
      <div className="flex-shrink-0 bg-mist/60 border-t border-mist px-4 py-2 flex items-center gap-2 text-xs text-ink/50">
        <span className="font-mono truncate">{previewSrc}</span>
        <span className="ml-auto whitespace-nowrap">
          Canlı: <a href={productionUrl} target="_blank" rel="noopener" className="text-brand-pink hover:underline">{productionUrl}</a>
        </span>
      </div>
    </div>
  );
}
